<template>
    <div class="time-period-settings-grid">
        <label for="timezone">Timezone</label>
        <time-zone-selector input-id="timezone" :model-value="currentTimezone" iana @update:model-value="updateSelectedTimezone" />
        <label for="periodFrequency">Period frequency</label>
        <select-button
            id="periodFrequency"
            aria-label="Select period frequency"
            option-label="name"
            option-value="id"
            placeholder="Select period frequency"
            :model-value="selectedPeriodFrequency"
            :options="PERIOD_FREQUENCY_OPTIONS"
            @update:model-value="updateSelectedPeriodFrequency"
        >
            <template #option="{ option }">
                <span class="select-button-option">
                    <span class="select-button-option-label">{{ option.name }}</span>
                    <info-icon-with-tooltip v-if="option.info" :tooltip-text="option.info" />
                </span>
            </template>
        </select-button>
        <div class="sub-section">
            <h4>Period options:</h4>
            <div class="sub-row">
                <label for="autoRollOver">Auto Rollover</label>
                <checkbox
                    :model-value="selectedAutoRollover"
                    input-id="autoRollOver"
                    binary
                    @update:model-value="updateSiteSetting('autoRollover', $event)"
                />
                <label for="showTimer">Show Timer</label>
                <checkbox
                    :model-value="selectedShowTimer"
                    input-id="showTimer"
                    binary
                    @update:model-value="updateSiteSetting('showTimer', $event)"
                />
                <label for="periodToUse">Site wide</label>
                <select-button
                    :model-value="selectedPeriodToUseSiteWide"
                    id="periodToUse"
                    :options="periodToUseSiteWideOptions"
                    option-label="name"
                    option-value="id"
                    :allow-empty="false"
                    @update:model-value="updatePeriodPickerWindowToUse($event, 'all')"
                />
                <label for="periodToUseBoaDashboard">BOA Visualiser - Overview</label>
                <select-button
                    :model-value="selectedPeriodToUseBoaDashboard ?? 'inherited'"
                    id="periodToUseBoaDashboard"
                    :options="periodToUsePageOverrideOptions"
                    option-label="name"
                    option-value="id"
                    :allow-empty="false"
                    @update:model-value="updatePeriodPickerWindowToUse($event, 'boa')"
                >
                    <template #option="{ option }">
                        <span class="select-button-option">
                            <span class="select-button-option-label">{{ option.name }}</span>
                            <info-icon-with-tooltip v-if="option.info" :tooltip-text="option.info" />
                        </span>
                    </template>
                </select-button>
                <label for="periodToUseBoaComparer">BOA Visualiser - Comparer</label>
                <select-button
                    :model-value="selectedPeriodToUseBoaComparer ?? 'inherited'"
                    id="periodToUseBoaComparer"
                    :options="periodToUsePageOverrideOptions"
                    option-label="name"
                    option-value="id"
                    :allow-empty="false"
                    @update:model-value="updatePeriodPickerWindowToUse($event, 'boaComparer')"
                >
                    <template #option="{ option }">
                        <span class="select-button-option">
                            <span class="select-button-option-label">{{ option.name }}</span>
                            <info-icon-with-tooltip v-if="option.info" :tooltip-text="option.info" />
                        </span>
                    </template>
                </select-button>
                <label for="periodToUsePlantActivity">Plant Activity</label>
                <select-button
                    :model-value="selectedPeriodToUsePlantActivity ?? 'inherited'"
                    id="periodToUsePlantActivity"
                    :options="periodToUsePageOverrideOptions"
                    option-label="name"
                    option-value="id"
                    :allow-empty="false"
                    @update:model-value="updatePeriodPickerWindowToUse($event, 'plantActivityBoard')"
                >
                    <template #option="{ option }">
                        <span class="select-button-option">
                            <span class="select-button-option-label">{{ option.name }}</span>
                            <info-icon-with-tooltip v-if="option.info" :tooltip-text="option.info" />
                        </span>
                    </template>
                </select-button>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import Checkbox from 'primevue/checkbox';
import SelectButton from 'primevue/selectbutton';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import TimeZoneSelector from '@/components/powerMarkets/selectors/TimeZoneSelector.vue';
import { PERIOD_FREQUENCY_OPTIONS } from '@/constants/PeriodFrequencyOptions.ts';
import { DEFAULT_PERIODS_PER_DAY, DEFAULT_TIMEZONE } from '@/constants/Time.ts';
import { periodToUsePageOverrideOptions, periodToUseSiteWideOptions } from '@/constants/powerMarkets/periodPickerOverrides.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { ISiteSettings } from '@/interfaces/powerMarkets/settings/ISiteSettings.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { WindowType } from '@/types/powerMarkets/balancing/WindowType.ts';
import { PeriodTypeOverridesWithInherited } from '@/types/powerMarkets/balancing/PeriodTypeOverrides.ts';

const userSettingsStore = useUserSettingsStore();

const selectedPeriodFrequency = ref<string>(userSettingsStore.userSettings.otherSettings.data.periodsInDayForCharts?.toString() ?? DEFAULT_PERIODS_PER_DAY.toString());

const siteSettings = computed<ISiteSettings>(() => userSettingsStore.userSettings.otherSettings.data);
const selectedPeriodToUseSiteWide = computed<string>(() => siteSettings.value.boaWindowType.all ?? 'current');
const selectedPeriodToUseBoaDashboard = computed<string | undefined>(() => siteSettings.value.boaWindowType.boa);
const selectedPeriodToUseBoaComparer = computed<string | undefined>(() => siteSettings.value.boaWindowType.boaComparer);
const selectedPeriodToUsePlantActivity = computed<string | undefined>(() => siteSettings.value.boaWindowType.plantActivityBoard);
const selectedAutoRollover = computed<boolean>(() => siteSettings.value.autoRollover);
const selectedShowTimer = computed<boolean>(() => siteSettings.value.showTimer);

const currentTimezone = computed<IIdName>(() => ({
    id: siteSettings.value.timezone ?? DEFAULT_TIMEZONE,
    name: siteSettings.value.timezone ?? DEFAULT_TIMEZONE,
}));

function updatePeriodPickerWindowToUse (value: PeriodTypeOverridesWithInherited, keyToUpdate: WindowType): void {
    const boaWindowType = { ...siteSettings.value.boaWindowType };
    if (value === 'inherited') {
        delete boaWindowType[keyToUpdate];
    } else {
        boaWindowType[keyToUpdate] = value;
    }

    updateSiteSetting('boaWindowType', boaWindowType);
}

function updateSelectedTimezone (newTimezone: IIdName): void {
    updateSiteSetting('timezone', newTimezone.id);
}

function updateSelectedPeriodFrequency (newPeriodFrequency: string): void {
    selectedPeriodFrequency.value = newPeriodFrequency;
    updateSiteSetting('periodsInDayForCharts', Number(newPeriodFrequency));
}

function updateSiteSetting<K extends keyof ISiteSettings> (prop: K, newSetting: ISiteSettings[K]): void {
    userSettingsStore.setSiteSettings(prop, newSetting, true);
}
</script>

<style lang="scss" scoped>

.time-period-settings-grid {
    align-items: center;
    display: grid;
    gap: var(--defaultSpacing) var(--largeSpacing);
    grid-template-columns: auto 1fr;

    @include responsive(sm) {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        :deep(.p-selectbutton) {
            width: 100%;
            max-width: 100%;
        }

        :deep(.p-togglebutton) {
            flex: 1 1 0;
            min-width: 0;
        }

        :deep(.p-togglebutton-content) {
            white-space: normal;
            text-align: center;
        }
    }

}

.sub-section {
    padding-top: var(--largeSpacing);
    grid-column: span 2;

    .sub-row {
        padding-left: var(--defaultSpacing);
        align-items: center;
        display: grid;
        gap: var(--defaultSpacing);
        grid-template-columns: auto 1fr;
        margin-bottom: var(--defaultSpacing);

        @include responsive(sm) {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            width: 100%;
        }
    }
}

h4 {
    margin-bottom: var(--largeSpacing);
}

label {
    font-size: var(--defaultFontSize);
    user-select: none;
}
</style>
